import React, { useState } from "react";
import { ArrowRight, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import InfoTooltip from "./admin/InfoTooltip";

/**
 * NewsletterSignup — public double-opt-in form for Ekselens Insights.
 * Captures email + consent, calls POST /api/newsletter/subscribe which sends
 * a Resend confirmation email. We show a success state regardless of whether
 * the email is already subscribed (don't leak subscriber existence).
 */
const NewsletterSignup = () => {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Please confirm consent to subscribe.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/newsletter/subscribe", { email, consent: true, source: "insights" });
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Subscription failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="bg-black text-white ek-section relative overflow-hidden"
      data-testid="newsletter-signup"
    >
      <div className="ek-star-watermark-left" />
      <div className="ek-container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="ek-eyebrow text-[#A179FA]">Newsletter</div>
            <h2 className="ek-heading text-3xl md:text-4xl lg:text-5xl text-white mt-4 leading-tight">
              One practical idea on transformation, every other Friday.
            </h2>
            <p className="text-zinc-300 font-light text-lg leading-relaxed mt-6 max-w-xl">
              Written for executives, transformation leaders and PMO directors. Short, focused, never promotional.
              You can unsubscribe with one click from any email.
            </p>
            <ul className="mt-6 space-y-2 text-zinc-400 text-[14px]">
              <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[#A179FA] mt-2 shrink-0" /> Every other Friday — never more</li>
              <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[#A179FA] mt-2 shrink-0" /> 3-minute read, one actionable idea</li>
              <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[#A179FA] mt-2 shrink-0" /> Double opt-in. No spam. No sharing.</li>
            </ul>
          </div>

          <div className="lg:col-span-6">
            {submitted ? (
              <div
                className="bg-white/5 border border-white/15 p-8 lg:p-10"
                data-testid="newsletter-success"
              >
                <Mail size={28} strokeWidth={1.4} className="text-[#A179FA]" />
                <h3 className="ek-heading text-xl md:text-2xl text-white mt-4">Check your inbox.</h3>
                <p className="text-zinc-300 font-light mt-3 text-[15px] leading-relaxed">
                  We've sent a confirmation email to <strong>{email}</strong>. Click the link inside to activate your subscription. (If you don't see it within a minute or two, check your spam folder.)
                </p>
              </div>
            ) : (
              <form
                onSubmit={submit}
                className="bg-white/5 border border-white/15 p-8 lg:p-10"
                data-testid="newsletter-form"
              >
                <label className="block">
                  <span className="text-[11px] font-medium tracking-[0.16em] text-zinc-400 uppercase">Work email</span>
                  <InfoTooltip testid="newsletter-email-info">
                    Use your work email so we can tailor the occasional example to your sector. It's double opt-in — you'll confirm via a link before anything is sent, and you can unsubscribe in one click.
                  </InfoTooltip>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="mt-3 w-full bg-transparent border border-white/25 focus:border-[#A179FA] px-4 py-3 text-white text-[16px] outline-none transition-colors"
                    data-testid="newsletter-email"
                  />
                </label>
                <label className="flex items-start gap-3 mt-5 text-[13px] text-zinc-300">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    data-testid="newsletter-consent"
                    className="mt-1 accent-[#7737F6]"
                  />
                  <span>
                    I agree to receive Ekselens Insights and accept the{" "}
                    <a href="/privacy" className="text-[#A179FA] underline">privacy policy</a>.
                  </span>
                </label>
                <button
                  type="submit"
                  disabled={busy}
                  className="ek-btn ek-btn-primary mt-6 disabled:opacity-60"
                  data-testid="newsletter-submit"
                >
                  {busy ? "Subscribing…" : "Subscribe"}
                  {!busy && <ArrowRight size={16} />}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsletterSignup;
